// components/SensorDetailPage.js - Updated for new BME688 step-based structure
import React, { useEffect } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';
import CircularProgress from '@mui/material/CircularProgress';
import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import Divider from '@mui/material/Divider';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import SensorRow from './SensorRow';
import BME688Sensor from './BME688Sensor';
import EvaluationSettingsPanel from './EvaluationSettingsPanel';
import EvaluationResultsPanel from './EvaluationResultsPanel';
import { useLatestMeasurements } from '../hooks/useMeasurements';
import { apiUtils } from '../utils/apiUtils';

// Enhanced function to separate BME688 from other sensors
function organizeSensorData(sensors) {
  console.log('🔍 organizeSensorData input:', sensors);
  console.log('🔍 Input keys:', Object.keys(sensors));

  const regularSensors = {};
  let bme688Data = null;

  // Skip metadata fields
  const metadataFields = ['summary', 'metadata', 'timestamp', 'measurement_id', 'box_id'];

  Object.entries(sensors).forEach(([sensorName, sensorData]) => {
    // Skip metadata
    if (metadataFields.includes(sensorName)) {
      console.log(`⏭️ Skipping metadata field: ${sensorName}`);
      return;
    }

    console.log(`📊 Processing sensor: ${sensorName}`, {
      isArray: Array.isArray(sensorData),
      type: typeof sensorData,
      hasData: sensorData !== null && sensorData !== undefined,
      dataPreview: Array.isArray(sensorData)
        ? `Array with ${sensorData.length} items`
        : typeof sensorData === 'object'
          ? `Object with keys: ${Object.keys(sensorData).join(', ')}`
          : String(sensorData)
    });

    // Check if this is BME688 - NEW STRUCTURE: organized by STEPS (0-9)
    if (sensorName === 'bme688') {
      if (typeof sensorData === 'object' && sensorData !== null && !Array.isArray(sensorData)) {
        const keys = Object.keys(sensorData);

        // Check if keys are numeric (step numbers: 0, 1, 2, ..., 9)
        const isNumericKeys = keys.length > 0 && keys.every(k => !isNaN(parseInt(k)));

        if (isNumericKeys) {
          // Validate that at least one step contains an array of sensors
          const hasValidStepData = keys.some(stepKey => {
            const stepData = sensorData[stepKey];
            return Array.isArray(stepData) && stepData.length > 0;
          });

          if (hasValidStepData) {
            bme688Data = sensorData;
            console.log('✅ BME688 detected with step-based structure:', {
              stepCount: keys.length,
              steps: keys,
              firstStepSensorCount: Array.isArray(sensorData[keys[0]]) ? sensorData[keys[0]].length : 0,
              structure: 'steps (0-9) each containing array of 8 sensors'
            });
          } else {
            console.log('⚠️ BME688 has numeric keys but no valid step data');
          }
        } else {
          console.log('⚠️ BME688 has unexpected structure - non-numeric keys:', keys);
        }
      } else {
        console.log('⚠️ BME688 has unexpected type:', typeof sensorData);
      }
    }
    // Regular sensor - must be an object with data (and not bme688)
    else if (sensorData !== null && sensorData !== undefined) {
      // For objects, check if they have keys
      if (typeof sensorData === 'object' && !Array.isArray(sensorData)) {
        const keys = Object.keys(sensorData);
        if (keys.length > 0) {
          regularSensors[sensorName] = sensorData;
          console.log(`📋 Added ${sensorName} as regular sensor with keys:`, keys);
        } else {
          console.log(`⚠️ Skipping ${sensorName} - empty object`);
        }
      } else if (Array.isArray(sensorData)) {
        console.log(`⚠️ Skipping ${sensorName} - unexpected array (not BME688)`);
      }
    } else {
      console.log(`⚠️ Skipping ${sensorName} - null or undefined`);
    }
  });

  console.log('✨ Final organization result:', {
    regularSensorCount: Object.keys(regularSensors).length,
    regularSensorNames: Object.keys(regularSensors),
    bme688Present: !!bme688Data,
    bme688Structure: bme688Data ? {
      stepCount: Object.keys(bme688Data).length,
      steps: Object.keys(bme688Data),
      sampleStepSensorCount: Array.isArray(bme688Data['0']) ? bme688Data['0'].length : 'N/A'
    } : null
  });

  return { regularSensors, bme688Data };
}

export default function SensorDetailPage({ selectedBox, onBack, onCompositeGraphClick, onTimeSeriesClick }) {
  console.log('🚀 SensorDetailPage render - selectedBox:', selectedBox);

  const {
    data: measurementData,
    loading,
    error,
    retry,
    refetch
  } = useLatestMeasurements(selectedBox?.box_id);

  console.log('📦 useLatestMeasurements result:', {
    hasData: !!measurementData,
    loading,
    error: error?.message,
    measurementData
  });

  if (loading) {
    return (
      <Box sx={{
        width: '100%',
        maxWidth: '100%',
        minHeight: '100vh',
        bgcolor: 'grey.50',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center'
      }}>
        <Box sx={{ textAlign: 'center' }}>
          <CircularProgress size={40} sx={{ mb: 2 }} />
          <Typography variant="body1" color="text.secondary">
            Loading sensor data...
          </Typography>
        </Box>
      </Box>
    );
  }

  if (error) {
    const canRetry = apiUtils.isRetryableError(error);
    return (
      <Box sx={{
        width: '100%',
        maxWidth: '100%',
        minHeight: '100vh',
        bgcolor: 'grey.50',
        p: 3
      }}>
        <Box sx={{
          display: 'flex',
          alignItems: 'center',
          mb: 4,
          bgcolor: 'white',
          p: 3,
          borderRadius: 2,
          boxShadow: 1
        }}>
          <IconButton onClick={onBack} sx={{ mr: 2 }}>
            <ArrowBackIcon />
          </IconButton>
          <Typography component="h1" variant="h4" sx={{ fontWeight: 600 }}>
            Sensors for Box {selectedBox?.title || selectedBox?.box_id}
          </Typography>
        </Box>

        <Alert
          severity="error"
          sx={{ mb: 2 }}
          action={
            canRetry ? (
              <Button color="inherit" size="small" onClick={retry}>
                Retry
              </Button>
            ) : null
          }
        >
          {apiUtils.formatErrorMessage(error)}
        </Alert>
      </Box>
    );
  }

  // Process sensor data from API response
  const rawSensors = measurementData?.measurement_data || {};
  console.log('🔍 Raw measurement_data from API:', rawSensors);
  console.log('🔍 Raw measurement_data keys:', Object.keys(rawSensors));

  const { regularSensors, bme688Data } = organizeSensorData(rawSensors);

  const regularSensorEntries = Object.entries(regularSensors);
  const totalSensorCount = regularSensorEntries.length + (bme688Data ? 1 : 0);

  console.log('🎯 Final rendering decision:', {
    totalSensors: totalSensorCount,
    regularSensors: regularSensorEntries.length,
    regularSensorNames: regularSensorEntries.map(([name]) => name),
    hasBME688: !!bme688Data,
    bme688StepCount: bme688Data ? Object.keys(bme688Data).length : 0
  });

  return (
    <Box sx={{
      width: '100%',
      maxWidth: '100%',
      minHeight: '100vh',
      bgcolor: 'grey.50'
    }}>
      {/* Header Section */}
      <Box sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        mb: 4,
        bgcolor: 'white',
        p: 3,
        borderRadius: 2,
        boxShadow: 1
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <IconButton onClick={onBack} sx={{ mr: 2 }}>
            <ArrowBackIcon />
          </IconButton>
          <Box>
            <Typography component="h1" variant="h4" sx={{ fontWeight: 600, mb: 1, fontSize: '1.8rem' }}>
              Sensors for {selectedBox?.title || `Box ${selectedBox?.box_id}`}
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ mb: 0.5, fontSize: '1.1rem' }}>
              Box ID: {measurementData?.box_id || selectedBox?.box_id} • {totalSensorCount} sensor type{totalSensorCount !== 1 ? 's' : ''} found
            </Typography>
            {measurementData?.measurement_id && (
              <Typography variant="body2" color="text.secondary" sx={{ fontSize: '1rem' }}>
                Measurement ID: {measurementData.measurement_id}
              </Typography>
            )}
            {measurementData?.timestamp && (
              <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.9rem' }}>
                Last updated: {new Date(measurementData.timestamp).toLocaleString()}
              </Typography>
            )}
          </Box>
        </Box>

        <Button
          variant="outlined"
          size="small"
          onClick={refetch}
          disabled={loading}
        >
          Refresh
        </Button>
      </Box>

      {totalSensorCount === 0 ? (
        <Box sx={{ p: 3 }}>
          <Alert severity="info">
            No active sensors found for this box. The sensors might be offline or no measurements are available.
          </Alert>
        </Box>
      ) : (
        <>
          {/* Evaluation Settings and Results */}
          <Box sx={{
            mb: 3,
            width: '100%',
            px: 3
          }}>
            <EvaluationSettingsPanel boxId={selectedBox?.box_id} />
            <Box sx={{ mt: 2 }}>
              <EvaluationResultsPanel boxId={selectedBox?.box_id} />
            </Box>
          </Box>

          <Divider sx={{ mb: 3 }} />

          {/* Individual Sensor Details */}
          <Box sx={{ px: 3 }}>
            <Typography variant="h5" sx={{ mb: 3, fontWeight: 600 }}>
              Sensor Measurements
            </Typography>

            {/* BME688 Sensor - NEW STEP-BASED STRUCTURE */}
            {bme688Data && (
              <Box sx={{ mb: 2 }}>
                <BME688Sensor
                  sensorData={bme688Data}
                  onCompositeGraphClick={onCompositeGraphClick}
                />
              </Box>
            )}

            {/* Regular sensors */}
            {regularSensorEntries.map(([sensorName, sensorData]) => (
              <Box key={sensorName} sx={{ mb: 2 }}>
                <SensorRow
                  sensorName={sensorName}
                  sensorData={sensorData}
                  onTimeSeriesClick={onTimeSeriesClick}
                  onCompositeGraphClick={onCompositeGraphClick}
                />
              </Box>
            ))}
          </Box>
        </>
      )}
    </Box>
  );
}